import Axios from 'axios';
import { call, cancel, put, take, takeLatest } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';

import { getLogger } from 'utils/logger';
import * as authActions from 'utils/auth/actions';
import { AuthTokenData } from 'utils/auth/types';
import { Config, HOSTNAME, ENVIRONMENT } from 'utils/webappConfig';
import { SessionStateError } from 'exceptions';
import { v4 as uuidv4 } from 'uuid';
import tracker from 'utils/tracker';
import * as sessionStorageEx from 'utils/sessionStorageEx';

const log = getLogger('containers/App/Login/sagas.js');
const WITH_EXTRA_QUERIES = 2;

export function* retrieveOauthToken({ payload: code }) {
  try {
    log.log('Retrieving oAuth Token');
    const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/oauth/token`;
    const query = window.location.href;
    const numQueries = query.split('?').length - 1;
    let finalRedirect = '';
    if(query.search('force') > 10){
      const redirectStr = query.substr(query.indexOf('=') + 1).split('?');
      if(numQueries > WITH_EXTRA_QUERIES){
        finalRedirect = redirectStr[0].concat('?',redirectStr[1])
      } else {
        finalRedirect = redirectStr[0];
      }
    }

    // todo: remove responseType
    const requestBody = {
      clientId: Config.environments[ENVIRONMENT].client_id,
      clientSecret: Config.environments[ENVIRONMENT].client_secret,
      grantType: 'authorization_code',
      code,
      redirectUri: finalRedirect || Config.hosts[HOSTNAME].redirect_uri,
      responseType: 'token',
    };

    let requestConfig = {};

    const sessionState = sessionStorageEx.getSessionState();
    sessionStorageEx.setSessionState(uuidv4());

    if (sessionState) {
      requestConfig = {
        headers: {
          'qcs-session-state': sessionState,
          'qcs-session-state-provided': 'true',
        },
      };
    }

    const { data, config } = yield call(Axios.post, requestUrl, requestBody, requestConfig);

    if (config && config.headers && config.headers['qcs-session-state']) {
      const sessionStateHeaderValue = config.headers['qcs-session-state'];
      if (sessionStateHeaderValue !== sessionState) {
        throw new SessionStateError({
          data: {
            errors: [
              {
                httpStatus: 401,
                detail: "QCS Unauthorized Access - Session state mismatch detected",
                code: "QCS-0401-16",
              },
            ],
          },
          message: 'session state mismatch',
        });
      }
    }

    const authTokenData = new AuthTokenData({
      accessToken: data.accessToken,
      accessTokenExpired: data.accessTokenExpired,
      refreshToken: data.refreshToken,
      refreshTokenExpired: data.refreshTokenExpired,
      scopes: data.scope,
      surveyRequested: data.surveyRequested,
    });

    yield put(authActions.getOAuthTokenSuccess(authTokenData));

    tracker.resetActiveTimestamp();
  } catch (error) {
    log.error(`Continued after catching error: ${JSON.stringify(error)}`);

    // {"response":{"data":{"errors":[{"httpStatus":401,"detail":"QCS Unauthorized Access - Session state mismatch detected","code":"QCS-0401-16"}]},"message":"session state mismatch"}}
    try {
      const exceptionData = error.response.data.errors[0];
      if (exceptionData.httpStatus === 401 && exceptionData.code === 'QCS-0401-16') {
        window.location.href = (`${Config.hosts[HOSTNAME].redirect_uri}?session_mismatch=1`);
      }
    } catch (exception) {
      // Do nothing
    }

    yield put(authActions.getOAuthTokenFailure(error));
  }
}

export default function* retrieveOauthTokenWatcher() {
  const watcher = yield takeLatest(authActions.getOAuthToken, retrieveOauthToken);
  // Suspend execution until location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watcher);
}
